import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import type { Timezones } from '@hero/schema';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Rules the backend uses to auto-stamp `Lead.nextFollowUpDate` when
 * a lead comes in through a public form (landing "Book a demo",
 * inbound contact, ...).
 *
 * Kept in the shared config so product can tune the SLA in one place
 * without redeploying the backend. Sales operates on Saudi hours;
 * Friday + Saturday are the weekend, so a Thursday-evening lead
 * queues for Sunday morning, not Friday.
 */
export const LEAD_FOLLOW_UP_CONFIG = {
  /**
   * Timezone used for the "before / after cutoff" decision. Saudi
   * sales team operates on Riyadh time regardless of where the lead
   * itself is browsing from.
   */
  timezone: 'Asia/Riyadh' satisfies Timezones,
  /**
   * Local hour (0–23, Saudi time) that flips the schedule from
   * "same day, one hour from now" to "tomorrow at 10 am". A lead
   * that arrives at 14:59 gets a 15:59 follow-up; one that arrives
   * at 15:01 gets tomorrow-10-am.
   */
  cutoffHour: 15,
  /**
   * Minutes to add to `now` when we're inside the same-day window
   * (before the cutoff). Product default: give the rep an hour to
   * finish whatever they're on and then jump on the fresh lead.
   */
  offsetMinutesBeforeCutoff: 60,
  /**
   * Local hour (0–23, Saudi time) the next-day / next-business-day
   * follow-up lands at.
   */
  nextDayHour: 10,
  /**
   * Weekend day-of-week values (0 = Sunday, ..., 6 = Saturday) that
   * we skip past when the follow-up would otherwise land on the
   * weekend. Saudi weekend = Friday + Saturday. Sunday is a normal
   * business day.
   */
  weekendDays: [5, 6] satisfies number[], // 5 = Fri, 6 = Sat
} as const;

export type LeadFollowUpConfig = typeof LEAD_FOLLOW_UP_CONFIG;

/**
 * Resolve the automatic `nextFollowUpDate` for a lead that just came
 * in through a public form.
 *
 * The rule (in {@link LEAD_FOLLOW_UP_CONFIG}):
 *  - Before the cutoff hour (Saudi local time) → `now + offsetMinutesBeforeCutoff`.
 *  - After the cutoff hour → the next business day at `nextDayHour:00`,
 *    skipping past Friday and Saturday to Sunday.
 *
 * All computation happens in the configured timezone and the result
 * is returned as a UTC `Date` (which is how MongoDB stores it).
 *
 * The `now` argument is injectable so unit tests can pin a specific
 * clock; production callers pass nothing and get the real wall clock.
 */
export function resolveLeadFollowUpDate(now: Date = new Date()): Date {
  const cfg = LEAD_FOLLOW_UP_CONFIG;
  const local = dayjs(now).tz(cfg.timezone);

  const isWeekend = (d: dayjs.Dayjs) =>
    (cfg.weekendDays as readonly number[]).includes(d.day());

  const beforeCutoff = local.hour() < cfg.cutoffHour;
  if (beforeCutoff && !isWeekend(local)) {
    return local.add(cfg.offsetMinutesBeforeCutoff, 'minute').toDate();
  }

  let next = local
    .add(1, 'day')
    .hour(cfg.nextDayHour)
    .minute(0)
    .second(0)
    .millisecond(0);

  // Skip weekend days until we land on a business day.
  while (isWeekend(next)) {
    next = next.add(1, 'day');
  }
  return next.toDate();
}
